import {FC, useEffect, useState} from 'react'
import Select, {StylesConfig} from 'react-select'
import {getAll} from '../../../../services/common_requests'

interface Props {
  selected: string
  onChange: (lang: string) => void
  /** narrow = legacy 15% width; block = full width in sticky toolbars */
  layout?: 'narrow' | 'block'
  /** If set, only these ISO codes are shown (e.g. menu translations en/fr/de). */
  allowedCodes?: string[]
}

interface LanguageOption {
  value: string
  label: string
  flag: string
}

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  de: 'Deutsch',
  ar: 'العربية',
}

const FLAG_MAPPING: Record<string, string> = {
  en: '/media/flags/united-states.svg',
  fr: '/media/flags/france.svg',
  es: '/media/flags/spain.svg',
  de: '/media/flags/germany.svg',
  ar: '/media/flags/arabic.svg',
}

const selectStyles: StylesConfig<LanguageOption, false> = {
  control: (base) => ({
    ...base,
    minHeight: 44,
    fontSize: 16,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 4000,
  }),
  menu: (base) => ({
    ...base,
    minWidth: 260,
    zIndex: 4000,
  }),
  option: (base) => ({
    ...base,
    fontSize: 16,
    whiteSpace: 'normal',
  }),
}

const LanguageSelector: FC<Props> = ({selected, onChange, layout = 'narrow', allowedCodes}) => {
  const [languages, setLanguages] = useState<LanguageOption[]>([])

  useEffect(() => {
    getAll({}, '/languages').then((data) => {
      if (Array.isArray(data)) {
        let codes = data as string[]
        codes = codes.filter((code) => code !== 'es')
        if (allowedCodes?.length) {
          codes = codes.filter((c) => allowedCodes.includes(c))
        }
        const formatted = codes.map((code: string) => ({
          value: code,
          label: LANGUAGE_NAMES[code] || code.toUpperCase(),
          flag: FLAG_MAPPING[code] || '',
        }))
        setLanguages(formatted)
      }
    })
  }, [allowedCodes])

  const wrapStyle =
    layout === 'block'
      ? {width: '100%', minWidth: 0, maxWidth: '100%'}
      : {width: '15%'}

  return (
    <div className='bo-lang-select' style={wrapStyle}>
      <Select<LanguageOption>
        className='fs-8 py-2'
        classNamePrefix='bo-select'
        options={languages}
        value={languages.find((lang) => lang.value === selected) || null}
        onChange={(selectedOption: LanguageOption | null) => onChange(selectedOption?.value || '')}
        getOptionLabel={(option: LanguageOption) => option.label}
        formatOptionLabel={(option: LanguageOption) => (
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            {option.flag ? <img src={option.flag} alt='' width={20} height={15} /> : null}
            <span>{option.label}</span>
          </div>
        )}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition='fixed'
        menuShouldScrollIntoView={false}
        styles={selectStyles}
      />
    </div>
  )
}

export {LanguageSelector}
